import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { BroadcastService } from '../broadcast/broadcast.service';
import { CommonEvents } from '../../enums/common-events.enum';
import { RepositoryState } from '../../enums/repository-state.enum';
import { CipherHelper, Encoder, AES, enc } from 'crypto-js';
import { IAppCache } from '../../types/iapp-cache';
import { environment } from '../../../../../environments/environment';
import { LogService } from '../log/log.service';
import { take } from 'rxjs/operators';
import { IndexedDbErrorCodes } from '../../enums/indexed-db-error-codes.enum';

@Injectable({
    providedIn: 'root'
})
export class RepositoryService {
    // TODO - figure out how to obtain, store and use public key
    public publicKey = 'testetetetete';

    private db: IDBDatabase;
    private openRequest: IDBOpenDBRequest;

    private state = RepositoryState.Initializing;

    private get crypto(): CipherHelper {
        return AES;
    }

    private get encoder(): Encoder {
        return enc.Utf8;
    }

    constructor(
        private broadcastService: BroadcastService,
        private logger: LogService
    ) {
        this.initDb();
    }

    public isReady() {
        return this.state >= RepositoryState.Ready;
    }

    public get(schema: string, id: string): Observable<any> {
        if (this.state === RepositoryState.NotAvailable) {
            return this.emptyObservable();
        } else if (this.state === RepositoryState.Initializing) { // if db is still being initialized wait for cache ready event
            return new Observable(subscriber => {
                this.broadcastService.on(CommonEvents.CacheReady)
                    .pipe(take(1))
                    .subscribe(() => {
                        const readyStorage = this.getStorage(schema, 'readonly');
                        this.getFromStorage(subscriber, readyStorage, id);
                    });
            });
        }

        const storage = this.getStorage(schema, 'readonly');
        return new Observable(subscriber => this.getFromStorage(subscriber, storage, id));
    }

    public getAll(schema: string, decrypted: boolean): Observable<any> {
        if (this.state === RepositoryState.NotAvailable) {

            return this.emptyObservable();
        } else if (this.state === RepositoryState.Initializing) { // if db is still being initialized wait for cache ready event

            return new Observable(subscriber => {
                this.broadcastService.on(CommonEvents.CacheReady)
                    .pipe(take(1))
                    .subscribe(() => {
                        const readyStorage = this.getStorage(schema, 'readonly');
                        this.getAllFromStorage(subscriber, readyStorage, decrypted);
                    });
            });
        }

        const storage = this.getStorage(schema, 'readonly');
        return new Observable(subscriber => {
            this.getAllFromStorage(subscriber, storage, decrypted);
        });
    }

    public put(schema: string, id: string, data: any): Observable<void> {
        if (this.state === RepositoryState.NotAvailable) {
            return this.emptyObservable();
        }

        const document: IAppCache = {
            id: environment.production ? btoa(id) : id,
            valid: 'true',
            created: Date.now(),
            data: environment.production ? this.encrypt(data) : data
        };

        return this.makeTransaction(this.getStorage(schema, 'readwrite').put(document));
    }

    public delete(schema: string, id: string): Observable<void> {
        if (this.state === RepositoryState.NotAvailable) {
            return this.emptyObservable();
        }
        return this.makeTransaction(this.getStorage(schema, 'readwrite').delete(environment.production ? btoa(id) : id));
    }

    private makeTransaction(transaction): Observable<void> {
        return new Observable(subscriber => {

            transaction.oncomplete = () => subscriber.next();

            transaction.onerror = () => subscriber.error();
        });
    }

    private emptyObservable(): Observable<void> {
        return new Observable<void>(subscriber => subscriber.next());
    }

    private getFromStorage(subscriber: Subscriber<any>, storage, id: string) {
        const getter = storage.get(environment.production ? btoa(id) : id);
        getter.onsuccess = (event) => {
            const cache = ((event.target as IDBRequest).result as IAppCache);

            if (!cache) {
                subscriber.next(null);
                return;
            }

            // Encryption is disabled in dev env, as switching between is environments is common consider all stored strings
            // as encrypted data. This should be reconsider in future or creation of another environment with disabled encryption
            // would be ideal
            const stored = environment.production || typeof cache.data === 'string' ? this.decrypt(cache.data) : cache.data;

            subscriber.next(stored);
        };

        getter.onerror = () => {
            subscriber.next(null);
        };
    }

    private getAllFromStorage(subscriber: Subscriber<any>, storage, decrypted) {
        const getter = storage.getAll();

        getter.onsuccess = (event) => {
            const cache = ((event.target as IDBRequest).result);

            if (!cache) {
                subscriber.next(null);
                return;
            }

            // Encryption is disabled in dev env, as switching between is environments is common consider all stored strings
            // as encrypted data. This should be reconsider in future or creation of another environment with disabled encryption
            // would be ideal
            const indexDbData = cache.map(cacheItem => {
                return environment.production || typeof cacheItem.data === 'string' ?
                    {
                        id: atob(cacheItem.id),
                        valid: cacheItem.valid,
                        data: decrypted ? this.decrypt(cacheItem.data) : cacheItem.data,
                        created: cacheItem.created
                    } : cacheItem;
            });

            subscriber.next(indexDbData);
        };

        getter.onerror = () => {
            subscriber.next(null);
        };
    }

    private getAllFromStorageEncrypted(subscriber: Subscriber<any>, storage) {
        const getter = storage.getAll();

        getter.onsuccess = (event) => {
            const cache = ((event.target as IDBRequest).result);

            if (!cache) {
                subscriber.next(null);
                return;
            }

            // Encryption is disabled in dev env, as switching between is environments is common consider all stored strings
            // as encrypted data. This should be reconsider in future or creation of another environment with disabled encryption
            // would be ideal
            const indexDbData = cache.map(cacheItem => {
                return environment.production || typeof cacheItem.data === 'string' ?
                    {
                        id: atob(cacheItem.id),
                        data: atob(cacheItem.data)
                    } : cacheItem;
            });
            subscriber.next(indexDbData);
        };

        getter.onerror = () => {
            subscriber.next(null);
        };
    }

    private initDb() {
        this.openRequest = window.indexedDB.open('cache', 1);

        this.openRequest.onsuccess = () => this.onSuccess();
        this.openRequest.onerror = (er) => this.onError((er.currentTarget as IDBOpenDBRequest).error);

        this.openRequest.onupgradeneeded = (event) => this.onUpgradeNeeded(event);

        // close connection when leaving/refreshing page
        window.onunload = () => {
            if (this.db) {
                this.db.close();
            }
        };
    }

    private getStorage(schema: string, mode: IDBTransactionMode) {
        return this.db.transaction(schema, mode).objectStore(schema);
    }

    private encrypt(data: any) {
        return this.crypto.encrypt(JSON.stringify(data), this.publicKey).toString();
    }

    private decrypt(encrypted): any {
        if (encrypted === null || encrypted === undefined) {
            return encrypted;
        }

        const decrypted = this.crypto.decrypt(encrypted, this.publicKey);
        const json = decrypted.toString(this.encoder);
        return json === '' ? null : JSON.parse(json);
    }

    private onSuccess() {
        this.db = this.openRequest.result;

        this.db.onerror = (e) => this.logger.error(e);

        this.state = RepositoryState.Ready;
        this.broadcastService.broadcast(CommonEvents.CacheReady);
    }

    private onError(er: DOMException) {
        if (er.code === IndexedDbErrorCodes.VersionMismatch) { // when local db is of higher version than what we wanna open
            window.indexedDB.deleteDatabase('cache');
            this.initDb();
        } else if (er.code === IndexedDbErrorCodes.OpenBlocked) { // happens in firefox in incognito mode
            this.setDbWorkaround();
        }
    }

    private onUpgradeNeeded(event: IDBVersionChangeEvent) {
        const db = (event.target as IDBOpenDBRequest).result;
        db.createObjectStore('api-cache', {keyPath: 'id'});
        db.createObjectStore('app-cache', {keyPath: 'id'});
    }

    private setDbWorkaround() {
        this.state = RepositoryState.NotAvailable;
    }
}
